/** ルーティング。アプリごとの画面はナビ定義から機械的に作る(R1 はプレースホルダ)。 */
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import { AuditPage } from "./admin/AuditPage";
import { SettingsPage } from "./admin/SettingsPage";
import { MailSession } from "./apps/dlp/MailSession";
import { MailMyPage, MailApprovalsPage, MailManagementPage, MailDetailPage } from "./apps/dlp/MailMessagesPage";
import { DLP } from "./apps/dlp/def";
import { RECEPTION } from "./apps/reception/def";
import { HR } from "./apps/hr/def";
import { IDP } from "./apps/idp/def";
import { AdminOpsLogPage } from "./apps/idp/LogsPage";
import { MDM } from "./apps/mdm/def";
import { AppIndex, AppLayout } from "./shell/AppLayout";
import { AppShell } from "./shell/AppShell";
import { DeviceCertDownload } from "./shell/DeviceCertDownload";
import { NotificationsPage } from "./shell/NotificationsPage";
import { AuthGate } from "./shell/AuthGate";
import { Forbidden, NotFound } from "./shell/Forbidden";
import { LicensePage } from "./shell/LicensePage";
import { Placeholder } from "./shell/Placeholder";
import { Home } from "./shell/Home";
import { PublicReport } from "./shell/PublicReport";
import { ManualHome } from "./shell/help/ManualHome";
import { ManualPage } from "./shell/help/ManualPage";
import { RequireAnyAdmin, RequireApp, RequireFeature, RequireSuiteAdmin } from "./shell/guards";
import type { AppDef } from "./shell/nav";

const APPS: AppDef[] = [HR, MDM, IDP, DLP, RECEPTION];

export default function App() {
  return (
    <Routes>
      {/* 社外の担当者が使う完了報告。トークンが鍵なのでログインの外に置く */}
      <Route path="r/:token" element={<PublicReport />} />

      {/*
        端末証明書の配布(/dc#<token>)。トークンは **fragment** で受けるので
        パスには何も入らない(アクセスログに残さない。fix1 の高 1)。
        ログインの外に置く — 受け取るのは端末を使う本人で、ログインの前だから。
      */}
      <Route path="dc" element={<DeviceCertDownload />} />

      <Route
        element={
          <AuthGate>
            <Outlet />
          </AuthGate>
        }
      >
        <Route element={<AppShell apps={APPS} />}>
          <Route index element={<Navigate to="/portal" replace />} />
          <Route path="portal" element={<Home />} />
          {/* お知らせはアプリ横断。ログインの中・アプリの外に置く */}
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="manual" element={<ManualHome />} />
          <Route path="manual/:module" element={<ManualPage />} />

          {/*
            管理者操作ログは「どれかのアプリの admin」なら見られる(R7c fix1 低1)。
            idp のロールは要らないので、`idp` 配下(RequireApp)ではなくここに置く。
            静的なパスなので `idp/*` より先に当たる。
          */}
          <Route
            path="idp/logs/admin-ops"
            element={
              <RequireAnyAdmin>
                <AdminOpsLogPage />
              </RequireAnyAdmin>
            }
          />

          <Route path="dlp/mail/my" element={<MailSession><MailMyPage /></MailSession>} />
          <Route path="dlp/mail/approvals" element={<MailSession><MailApprovalsPage /></MailSession>} />
          <Route path="dlp/mail/messages" element={<MailSession><MailManagementPage /></MailSession>} />
          <Route path="dlp/mail/messages/:uid" element={<MailSession><MailDetailPage /></MailSession>} />
          <Route path="dlp/mail/approve" element={<Navigate to="/dlp/mail/approvals" replace />} />
          <Route path="dlp/mail/held" element={<Navigate to="/dlp/mail/messages?status=held" replace />} />
          <Route path="dlp/mail/log" element={<Navigate to="/dlp/mail/messages" replace />} />

          {APPS.map((def) => (
            <Route
              key={def.key}
              path={def.key}
              element={
                <RequireApp appKey={def.key}>
                  <AppLayout />
                </RequireApp>
              }
            >
              <Route index element={<AppIndex def={def} />} />
              {def.groups.flatMap((g) =>
                g.items.map((it) => (
                  <Route
                    key={it.path}
                    path={it.path}
                    element={
                      <RequireFeature feature={it.feature}>
                        {it.component ? (
                          it.path.startsWith("mail/") ? <MailSession><it.component /></MailSession> : <it.component />
                        ) : (
                          <Placeholder title={it.label} phase={it.phase} appName={def.name} />
                        )}
                      </RequireFeature>
                    }
                  />
                )),
              )}
              {(def.extraRoutes ?? []).map((r) => (
                <Route key={r.path} path={r.path} element={<r.component />} />
              ))}
              <Route path="*" element={<NotFound />} />
            </Route>
          ))}

          <Route
            path="admin/audit"
            element={
              <RequireAnyAdmin>
                <AuditPage />
              </RequireAnyAdmin>
            }
          />
          {/*
            ライセンス。**アプリの外**(core)に置く — 契約が空でもここには
            入れないと、貼り替える手が無くなる
          */}
          <Route
            path="license"
            element={
              <RequireSuiteAdmin>
                <LicensePage />
              </RequireSuiteAdmin>
            }
          />
          <Route
            path="admin/settings"
            element={
              <RequireSuiteAdmin>
                <SettingsPage />
              </RequireSuiteAdmin>
            }
          />

          <Route path="403" element={<Forbidden />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}
